import Tag from '@sats-group/ui-lib/react/tag';
import Text from '@sats-group/ui-lib/react/text';
import cn from 'classnames';
import React from 'react';
import { MediaType } from '~/shared/media-type-helper';
import BackgroundImage from '../background-image/background-image';
import Indicator from '../indicator/indicator';
import {
  aspectRatios,
  type PreviewCard as Props,
  themes,
} from './preview-card.types';

/**
 * To be used in the future as standardised article card across site
 * @param theme
 * @default "standardPrimary"
 *
 *   Options:
 *    - "standardPrimary" – used on secondary backgrounds
 *    - "standardSecondary" – used on white backgrounds
 *    - "promotionalFixed" – used for promotional content
 *
 * @param aspectRatio
 * @default "fourFive"
 *
 *   Options:
 *    - "fourSix" - 4:6 portrait
 *    - "fourFive" – 4:5 portrait
 *    - "oneOne" – 1:1 square
 *
 * @param tags
 * @default []
 */
const PreviewCard: React.FC<Props> = ({
  aspectRatio = aspectRatios.fourFive,
  href,
  indicator,
  image,
  tags,
  text,
  title,
  theme = themes.standardPrimary,
}) => (
  <a className={cn('preview-card', `preview-card--${aspectRatio}`)} href={href}>
    {image.type === MediaType.Image ? (
      <BackgroundImage
        {...image.props}
        className="preview-card__image"
        focusArea={BackgroundImage.focusAreas.Face}
      />
    ) : null}

    {/* NOTE: this is for fallback image */}
    {image.type === MediaType.RawSvg ? (
      <div
        aria-hidden
        role="img"
        className="preview-card__raw-svg"
        dangerouslySetInnerHTML={{ __html: image.props }}
      />
    ) : null}

    {theme === themes.promotionalFixed ? (
      <div className="preview-card__overlay--dark-hover"></div>
    ) : null}

    <div
      className={cn('preview-card__content', `preview-card__content--${theme}`)}
    >
      <div className="preview-card__tags">
        {tags.map(tag => (
          <Tag
            variant={Tag.variants.secondary}
            {...tag}
            key={tag.text}
            size={Tag.sizes.xs}
          />
        ))}
      </div>
      <div
        className={cn(
          'preview-card__text-content',
          `preview-card__text-content--${theme}`
        )}
      >
        <div
          className={cn(
            'preview-card__title',
            `preview-card__title--${theme}`,
            {
              'preview-card__title--only': !text,
            }
          )}
        >
          {theme === themes.promotionalFixed ? (
            <Text
              italic
              theme={Text.themes.headline}
              size={Text.sizes.headline3}
              tight
            >
              {title}
            </Text>
          ) : (
            <Text
              theme={Text.themes.emphasis}
              size={Text.sizes.headline3}
              tight
            >
              {title}
            </Text>
          )}
        </div>
        {text ? (
          <div
            className={cn(
              'preview-card__description',
              `preview-card__description--${theme}`
            )}
          >
            <Text size={Text.sizes.small}>{text}</Text>
          </div>
        ) : null}
        {indicator ? <Indicator {...indicator} /> : null}
      </div>
    </div>
  </a>
);

export default PreviewCard;
